import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, User, Building2, ArrowRight, Briefcase } from 'lucide-react';
import axios from 'axios';
import { getStoredAttribution } from '../utils/attribution';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

/**
 * Unified login for Freddy Leads. Because the platform is now agency-managed
 * (Freddy Media runs campaigns on behalf of brands), the primary audience of
 * this door is **brands**. Executives can still log in / apply, but their
 * entry point is demoted to a small link at the bottom that toggles the form
 * into executive-mode.
 */
export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const initialAudience = searchParams.get('as') === 'executive' ? 'executive' : 'brand';
  const [audience, setAudience] = useState(initialAudience);
  const [tab, setTab] = useState(searchParams.get('tab') === 'register' ? 'register' : 'login');
  const [form, setForm] = useState({
    email: '', password: '', name: '', brandName: '', website: '', industry: '',
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, register, setUser } = useAuth();
  const navigate = useNavigate();

  const updateForm = (patch) => setForm(f => ({ ...f, ...patch }));

  const handleBrandLogin = async () => {
    const { data } = await axios.post(`${API}/portal/auth/login`, { email: form.email, password: form.password });
    localStorage.setItem('propost_brand_token', data.access_token);
    navigate('/portal');
  };

  const handleBrandRegister = async () => {
    if (!termsAccepted) { setError('You must accept the Terms of Service and Privacy Policy.'); return; }
    if (!form.brandName.trim()) { setError('Brand / company name is required.'); return; }
    const { data } = await axios.post(`${API}/portal/auth/signup`, {
      brand_name: form.brandName,
      contact_name: form.name,
      email: form.email,
      password: form.password,
      website: form.website,
      industry: form.industry,
      attribution: getStoredAttribution(),
    });
    localStorage.setItem('propost_brand_token', data.access_token);
    navigate('/portal?tab=settings');
  };

  const handleExecLogin = async () => {
    const userData = await login(form.email, form.password);
    if (userData?.role === 'admin') navigate('/admin');
    else if (userData?.status === 'pending' || !userData?.industries?.length) navigate('/onboarding');
    else navigate('/dashboard');
  };

  const handleExecRegister = async () => {
    if (!termsAccepted) { setError('You must accept the Terms of Service and Privacy Policy.'); return; }
    const userData = await register(form.email, form.password, form.name, 'executive', termsAccepted);
    if (userData?.status === 'pending' || !userData?.industries?.length) navigate('/onboarding');
    else navigate('/dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (audience === 'brand') {
        if (tab === 'login') await handleBrandLogin();
        else await handleBrandRegister();
      } else {
        if (tab === 'login') await handleExecLogin();
        else await handleExecRegister();
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Google auth still routes execs (keeps the same flow they have today).
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const isBrand = audience === 'brand';
  const isRegister = tab === 'register';

  return (
    <div className="min-h-screen bg-[#090E33] flex items-center justify-center px-4 py-8" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/pflz8b2v_freddy-logo-symbol.png" alt="Freddy Media" className="h-12 w-12 mx-auto mb-3" />
          <h1 className="font-display text-3xl font-bold mb-1">
            <span className="text-[#FF4343]">Freddy</span>Leads
          </h1>
          <p className="text-[#9CA3AF] text-xs">
            {isBrand ? 'Your dedicated campaign HQ' : 'Executive endorsement agency'}
          </p>
        </div>

        {/* Audience badge — communicates which door you're at */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <span
            data-testid="audience-badge"
            className={`inline-flex items-center gap-1.5 px-3 py-1 border text-[10px] font-mono uppercase tracking-wider ${isBrand ? 'border-[#FF4343] bg-[#FF4343]/10 text-[#FF4343]' : 'border-white/20 bg-white/5 text-white'}`}
          >
            {isBrand ? <Building2 className="w-3 h-3" /> : <Briefcase className="w-3 h-3" />}
            {isBrand ? 'Brand Portal' : 'Executive Portal'}
          </span>
        </div>

        <div className="glass p-6 sm:p-7" data-testid="auth-form-container">
          {/* Login / Register tabs */}
          <div className="flex border-b border-white/10 mb-5">
            <button
              onClick={() => { setTab('login'); setError(''); }}
              data-testid="login-tab"
              className={`flex-1 pb-3 text-sm font-medium transition-colors ${tab === 'login' ? 'text-[#FF4343] border-b-2 border-[#FF4343]' : 'text-[#9CA3AF] hover:text-white'}`}
            >
              Log In
            </button>
            <button
              onClick={() => { setTab('register'); setError(''); }}
              data-testid="register-tab"
              className={`flex-1 pb-3 text-sm font-medium transition-colors ${tab === 'register' ? 'text-[#FF4343] border-b-2 border-[#FF4343]' : 'text-[#9CA3AF] hover:text-white'}`}
            >
              {isBrand ? 'Sign Up' : 'Apply'}
            </button>
          </div>

          {/* Google button — only for executives (brands use email/pass or invite) */}
          {!isBrand && (
            <>
              <button
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
                className="w-full flex items-center justify-center gap-3 border border-white/20 bg-white/5 hover:bg-white/10 text-white py-3 text-sm font-medium transition-all duration-200 mb-5"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Continue with Google
              </button>
              <div className="flex items-center gap-4 mb-5">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-xs text-[#6B7280] font-mono uppercase tracking-wider">or</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
            </>
          )}

          {/* Brand-only concierge hint on register */}
          {isBrand && isRegister && (
            <div className="mb-4 bg-[#FF4343]/5 border border-[#FF4343]/20 px-3 py-2.5" data-testid="brand-concierge-hint">
              <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-[#FF4343] mb-0.5">Prefer a walkthrough?</p>
              <p className="text-xs text-[#9CA3AF]">
                <a href="https://meetings.hubspot.com/jon980" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#FF4343] underline">Book 20 min with Jon</a> — we&apos;ll spin up your portal, add a campaign manager, and map out your first launch.
              </p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Brand register: brand name */}
            {isBrand && isRegister && (
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                <input
                  type="text" placeholder="Brand / Company name" value={form.brandName}
                  onChange={(e) => updateForm({ brandName: e.target.value })} required
                  data-testid="brand-register-name"
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343] transition-colors"
                />
              </div>
            )}
            {/* Register: contact name (both audiences) */}
            {isRegister && (
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                <input
                  type="text" placeholder={isBrand ? 'Your name (point of contact)' : 'Full name'} value={form.name}
                  onChange={(e) => updateForm({ name: e.target.value })} required
                  data-testid="register-name-input"
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343] transition-colors"
                />
              </div>
            )}
            {/* Email */}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
              <input
                type="email" placeholder={isBrand ? 'Work email' : 'Email address'} value={form.email}
                onChange={(e) => updateForm({ email: e.target.value })} required
                data-testid="auth-email-input"
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343] transition-colors"
              />
            </div>
            {/* Password */}
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
              <input
                type="password" placeholder={isRegister ? 'Password (8+ chars)' : 'Password'} value={form.password}
                onChange={(e) => updateForm({ password: e.target.value })} required
                data-testid="auth-password-input"
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343] transition-colors"
              />
            </div>
            {/* Brand register: optional website + industry */}
            {isBrand && isRegister && (
              <>
                <input
                  type="text" placeholder="Website (optional)" value={form.website}
                  onChange={(e) => updateForm({ website: e.target.value })}
                  data-testid="brand-register-website"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343] transition-colors"
                />
                <input
                  type="text" placeholder="Industry (optional — e.g. SaaS, Fintech)" value={form.industry}
                  onChange={(e) => updateForm({ industry: e.target.value })}
                  data-testid="brand-register-industry"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343] transition-colors"
                />
              </>
            )}
            {/* Terms (register only) */}
            {isRegister && (
              <label className="flex items-start gap-2.5 cursor-pointer pt-1" data-testid="terms-checkbox-label">
                <input
                  type="checkbox" checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  data-testid="terms-checkbox"
                  className="mt-0.5 w-4 h-4 accent-[#FF4343] flex-shrink-0"
                />
                <span className="text-xs text-[#9CA3AF] leading-relaxed">
                  I agree to the <Link to="/terms" target="_blank" className="text-[#FF4343] hover:underline">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="text-[#FF4343] hover:underline">Privacy Policy</Link>
                </span>
              </label>
            )}
            <button
              type="submit" disabled={loading}
              data-testid="auth-submit-btn"
              className="w-full flex items-center justify-center gap-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] hover:bg-[#E03030] py-3 text-sm font-semibold transition-all duration-300 disabled:opacity-50 mt-2"
            >
              {loading ? 'Please wait…' : (
                isBrand
                  ? (isRegister ? 'Create Brand Portal' : 'Log Into Brand Portal')
                  : (isRegister ? 'Apply as an Executive' : 'Log In')
              )}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>

        {/* Audience toggle — demoted link */}
        <div className="text-center mt-5" data-testid="audience-toggle">
          {isBrand ? (
            <button
              onClick={() => { setAudience('executive'); setTab('login'); setError(''); }}
              data-testid="switch-to-executive"
              className="text-xs text-[#6B7280] hover:text-[#FF4343] transition-colors"
            >
              Are you an executive? Log in here &rarr;
            </button>
          ) : (
            <button
              onClick={() => { setAudience('brand'); setTab('login'); setError(''); }}
              data-testid="switch-to-brand"
              className="text-xs text-[#6B7280] hover:text-[#FF4343] transition-colors"
            >
              &larr; Back to Brand Portal
            </button>
          )}
          <div className="flex justify-center gap-3 mt-3">
            <Link to="/terms" className="text-[10px] text-[#6B7280] hover:text-white transition-colors">Terms</Link>
            <span className="text-[10px] text-[#6B7280]">&middot;</span>
            <Link to="/privacy" className="text-[10px] text-[#6B7280] hover:text-white transition-colors">Privacy</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
